import { BlankContainer } from "../molecules/blank-container";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../atoms/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useDemographies, DemographyItem } from "../../providers/demographies-provider";
import {useBackendUser, UserProvider} from "../../providers/user-provider";

const backgroundImage = "/images/background/onboard.png";

export const AddDemography = () => {
  const navigate = useNavigate();
  // @ts-ignore
  const { user } = useAuth();
  const { backendUser, refresh } = useBackendUser();
  const { demosResponse, isLoading, error, refresh: refreshDemos } = useDemographies();

  // Group demographies by type
  const optionsByType = useMemo(() => {
    const map = new Map<string, DemographyItem[]>();
    (demosResponse || []).forEach((d) => {
      if (!map.has(d.type)) map.set(d.type, []);
      map.get(d.type)!.push(d);
    });
    return map;
  }, [demosResponse]);

  // Track selection per type
  const [selectedByType, setSelectedByType] = useState<Record<string, string>>({}); // type -> demography id
  const allTypes = useMemo(() => Array.from(optionsByType.keys()), [optionsByType]);
  const allSelected = allTypes.length > 0 && allTypes.every((t) => !!selectedByType[t]);

  const [inactiveMessage, setInactiveMessage] = useState<string | null>(null);

  const handleSelect = (type: string, id: string) => {
    setSelectedByType((prev) => ({ ...prev, [type]: id }));
  };

  useEffect(() => {
    if (user) {
      refresh(user?.email).then()
      refreshDemos().then()
    }
  }, [user]);

  useEffect(() => {
    if (demosResponse?.length && backendUser?.demography_ids?.length) {
      // @ts-ignore
      const anyInactive = demosResponse.filter(d => backendUser.demography_ids.find((ud: string)=> ud === d.id)).some(d => !d.active);

      if (anyInactive) {
        setInactiveMessage("Thanks, at the moment we are not collecting info from your demography");
        return;
      }
      navigate("/instructions");
    }
  }, [backendUser, demosResponse]);

  const handleSubmit = async () => {
    // Evaluate if any selected demography is inactive
    const selectedItems: DemographyItem[] = allTypes
      .map((t) => (optionsByType.get(t) || []).find((d) => d.id === selectedByType[t]))
      .filter(Boolean) as DemographyItem[];

    try {
      const apiUrl = process.env.REACT_APP_API_URL as string;
      const patchUrl = `${apiUrl}/users/${user?.email}/`;
      const demography_ids = selectedItems.map((d) => d.id);
      await fetch(patchUrl, {
        method: "PATCH",
        headers: {
          Authorization: `bearer ${user?.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ demography_ids }),
      });
      await refresh(user?.email)
    } catch (e) {
      // Non-blocking: ignore errors for now
      console.warn("Failed to save demographies selection", e);
    }
  };

  return (
    <div
      className="w-full h-screen flex justify-center items-center flex-col"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPositionX: "center",
      }}
    >
      <h1 className="mb-6" style={{ fontSize: "1.4rem" }}>
        Tell us about you
      </h1>
      <BlankContainer showOverflow={true}>
        <div className="p-4 space-y-4">
          {isLoading && <div>Loading demographies…</div>}
          {error && (
            <div className="text-red-600 text-sm">Failed to load demographies: {error}</div>
          )}

          {!isLoading && !error && allTypes.length === 0 && (
            <div>No demographies available.</div>
          )}

          {!inactiveMessage && !isLoading && !error && allTypes.map((type) => (
            <div key={type} className="flex flex-col">
              <label className="text-sm font-medium mb-1 capitalize">{type}</label>
              <select
                className="border rounded px-3 py-2"
                value={selectedByType[type] || ""}
                onChange={(e) => handleSelect(type, e.target.value)}
              >
                <option value="" disabled>
                  Select {type}
                </option>
                {(optionsByType.get(type) || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          ))}

          {inactiveMessage && (
            <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 p-3 rounded">
              {inactiveMessage}
            </div>
          )}

          {!inactiveMessage ? <div className="pt-2">
            <Button disabled={!allSelected} onClick={handleSubmit} type={"primary"}>
              Submit demography info
            </Button>
          </div> : null}
        </div>
      </BlankContainer>
    </div>
  );
};
